class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.openedBy = null;
    this.overlay = null;
    this.panel = null;
    this.closeButtons = [];
    this._focusTrap = null;
    this.onCloseClick = this.onCloseClick.bind(this);
    this.onCartUpdate = this.onCartUpdate.bind(this);
    this.onEtoileQuickAdd = this.onEtoileQuickAdd.bind(this);
    this.onPreviewInsetChange = this.onPreviewInsetChange.bind(this);
    this.previewObserver = null;
    this.previewInset = -1;
    this.refreshVersion = 0;
  }

  connectedCallback() {
    this.overlay = this.querySelector("[data-cart-drawer-overlay]");
    this.panel = this.querySelector("[data-cart-drawer-panel]");
    this.closeButtons = Array.from(
      this.querySelectorAll("[data-cart-drawer-close]"),
    );

    if (this.overlay) {
      this.overlay.addEventListener("click", this.onCloseClick);
    }
    this.closeButtons.forEach((button) =>
      button.addEventListener("click", this.onCloseClick),
    );
    this.cartUpdateEvent =
      (window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) || "cart-update";
    document.addEventListener(this.cartUpdateEvent, this.onCartUpdate);
    this.addEventListener("click", this.onEtoileQuickAdd);
  }

  disconnectedCallback() {
    this.refreshVersion += 1;
    document.removeEventListener(this.cartUpdateEvent, this.onCartUpdate);
    this.removeEventListener("click", this.onEtoileQuickAdd);
    this.stopPreviewInset();
    if (this.overlay) {
      this.overlay.removeEventListener("click", this.onCloseClick);
    }
    this.closeButtons.forEach((button) =>
      button.removeEventListener("click", this.onCloseClick),
    );
  }

  onPreviewInsetChange() {
    if (!this.hasAttribute("open")) return;
    const width = window.innerWidth || document.documentElement.clientWidth;
    const height = window.innerHeight || document.documentElement.clientHeight;
    const visibleBottom = window.visualViewport
      ? window.visualViewport.offsetTop + window.visualViewport.height
      : height;
    let inset = 0;
    if (width <= 767) {
      ["#PBarNextFrameWrapper", "#preview-bar-iframe", "#PBarNextFrame"].forEach((selector) => {
        const frame = document.querySelector(selector);
        if (!frame || !frame.getClientRects().length) return;
        const rect = frame.getBoundingClientRect();
        const style = window.getComputedStyle ? window.getComputedStyle(frame) : null;
        if (style && (style.display === "none" || style.visibility === "hidden")) return;
        if (rect.height >= 24 && rect.height <= 180 && rect.width >= width * 0.6 &&
            rect.top > visibleBottom * 0.5 && rect.bottom >= visibleBottom - 12) {
          inset = Math.max(inset, Math.min(180, Math.max(0, height - rect.top)));
        }
      });
    }
    if (inset !== this.previewInset) {
      this.previewInset = inset;
      this.style.setProperty("--etoile-cart-preview-inset", `${inset}px`);
    }
  }

  startPreviewInset() {
    this.onPreviewInsetChange();
    window.addEventListener("resize", this.onPreviewInsetChange);
    if (window.visualViewport) window.visualViewport.addEventListener("resize", this.onPreviewInsetChange);
    if (!this.previewObserver && window.MutationObserver) {
      this.previewObserver = new MutationObserver(this.onPreviewInsetChange);
      this.previewObserver.observe(document.documentElement, { childList: true, subtree: true });
    }
  }

  stopPreviewInset() {
    window.removeEventListener("resize", this.onPreviewInsetChange);
    if (window.visualViewport) window.visualViewport.removeEventListener("resize", this.onPreviewInsetChange);
    if (this.previewObserver) {
      this.previewObserver.disconnect();
      this.previewObserver = null;
    }
  }


  async onEtoileQuickAdd(event) {
    const target = event.target && event.target.closest
      ? event.target.closest("[data-etoile-quick-add]") : null;
    if (!target || !this.contains(target) || target.disabled) return;
    const variantId = Number(target.getAttribute("data-variant-id"));
    if (!Number.isSafeInteger(variantId) || variantId <= 0) return;
    event.preventDefault();
    if (target.getAttribute("aria-busy") === "true") return;
    target.disabled = true;
    target.setAttribute("aria-busy","true");
    let errorBox = this.querySelector("[data-etoile-quick-add-error]");
    if (!errorBox) {
      errorBox = document.createElement("p");
      errorBox.className = "etoile-cart-quick-add-error";
      errorBox.setAttribute("data-etoile-quick-add-error","");
      errorBox.setAttribute("role","alert");
      const parent = target.closest(".cart-drawer__upsell-panel");
      if (parent) parent.appendChild(errorBox);
    }
    if (errorBox) errorBox.textContent = "";
    try {
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
      const response = await fetch(root.replace(/\/?$/, "/") + "cart/add.js", {
        method:"POST", credentials:"same-origin",
        headers:{"Content-Type":"application/json","Accept":"application/json"},
        body:JSON.stringify({items:[{id:variantId,quantity:1}]})
      });
      if (!response.ok) {
        const detail = await response.json().catch(() => ({}));
        throw new Error(detail.description || detail.message || "Unable to add this item");
      }
      const refreshed = await this.refreshCart();
      if (!refreshed) throw new Error("Cart display could not refresh");
    } catch(error) {
      if (errorBox && errorBox.isConnected) {
        errorBox.textContent = error && error.message ? error.message : "Unable to add this item";
      }
    } finally {
      if (target.isConnected) {
        target.disabled = false;
        target.removeAttribute("aria-busy");
      }
    }
  }

  onCloseClick() {
    this.hide();
  }

  onCartUpdate(event) {
    const sections = event.detail && event.detail.cart && event.detail.cart.sections;
    if (sections && this.renderCartSections(sections)) {
      this.refreshVersion += 1;
      this.removeAttribute("aria-busy");
      return;
    }
    this.refreshCart();
  }

  renderCartSections(sections) {
    const sectionId = this.dataset.sectionId;
    const html = sections && sections[sectionId];
    if (!html) return false;
    const parsed = new DOMParser().parseFromString(html, "text/html");
    const source = parsed.querySelector("[data-cart-drawer-content]");
    const target = this.querySelector("[data-cart-drawer-content]");
    const summarySource = parsed.querySelector("[data-cart-drawer-summary]");
    const summaryTarget = this.querySelector("[data-cart-drawer-summary]");
    if (!source || !target || !summarySource || !summaryTarget) return false;
    target.innerHTML = source.innerHTML;
    summaryTarget.innerHTML = summarySource.innerHTML;
    summaryTarget.className = summarySource.className;
    const progressSource = parsed.querySelector("free-shipping-progress");
    const progressTarget = this.querySelector("free-shipping-progress");
    if (progressSource && progressTarget) {
      progressTarget.dataset.totalAmount = progressSource.dataset.totalAmount || "";
    }
    const bubble = document.getElementById("Cart-Icon-Bubble");
    if (bubble && typeof sections["cart-icon-bubble"] === "string") {
      const bubbleDocument = new DOMParser().parseFromString(
        sections["cart-icon-bubble"], "text/html",
      );
      const bubbleSource = bubbleDocument.querySelector(".shopify-section");
      bubble.innerHTML = bubbleSource ? bubbleSource.innerHTML : sections["cart-icon-bubble"];
    }
    if (window.webvista && typeof window.webvista.initLazyImageAnimation === "function") {
      window.webvista.initLazyImageAnimation(target);
    }
    if (typeof this.refreshUpsell === "function") this.refreshUpsell();
    const errorLink = this.querySelector("[data-cart-refresh-error]");
    if (errorLink) errorLink.hidden = true;
    return true;
  }

  refreshCart() {
    const sectionId = this.dataset.sectionId;
    if (!sectionId) return Promise.resolve(false);
    const version = ++this.refreshVersion;
    const url = new URL(window.location.href);
    url.hash = "";
    url.searchParams.delete("section_id");
    url.searchParams.set("sections", sectionId + ",cart-icon-bubble");
    this.setAttribute("aria-busy", "true");
    return fetch(url.toString(), {
      credentials: "same-origin",
      cache: "no-store",
      headers: { "X-Requested-With": "XMLHttpRequest" },
    })
      .then((response) => {
        if (!response.ok) throw new Error("Cart section request failed");
        return response.json();
      })
      .then((sections) => {
        if (version !== this.refreshVersion || !this.isConnected) return false;
        if (!this.renderCartSections(sections)) throw new Error("Cart section unavailable");
        return true;
      })
      .catch(() => {
        if (version === this.refreshVersion && this.isConnected) {
          const errorLink = this.querySelector("[data-cart-refresh-error]");
          if (errorLink) errorLink.hidden = false;
        }
        return false;
      })
      .then((result) => {
        if (version === this.refreshVersion) this.removeAttribute("aria-busy");
        return result;
      });
  }

  show(opener) {
    this.openedBy = opener || null;
    this.setAttribute("open", "");
    this.startPreviewInset();
    document.documentElement.classList.add("overflow-hidden");
    if (this.overlay) {
      this.overlay.classList.add("opacity-100");
      this.overlay.classList.remove("opacity-0");
    }
    if (this.panel) {
      this.panel.classList.add("!translate-x-0");
    }
    this.classList.remove("pointer-events-none");
    this.refreshCart();
    if (!this._focusTrap && window.webvista && typeof window.webvista.createFocusTrap === "function") {
      this._focusTrap = window.webvista.createFocusTrap(this, {
        onEscape: this.onCloseClick,
      });
    }
    if (this._focusTrap) this.addEventListener("keydown", this._focusTrap);
    if (window.webvista && typeof window.webvista.focusFirstElement === "function") {
      window.webvista.focusFirstElement(this);
    }
  }

  hide() {
    this.removeAttribute("open");
    this.stopPreviewInset();
    document.documentElement.classList.remove("overflow-hidden");
    if (this.overlay) {
      this.overlay.classList.remove("opacity-100");
      this.overlay.classList.add("opacity-0");
    }
    if (this.panel) {
      this.panel.classList.remove("!translate-x-0");
    }
    this.classList.add("pointer-events-none");
    if (this._focusTrap) {
      this.removeEventListener("keydown", this._focusTrap);
    }
    if (
      this.openedBy &&
      typeof this.openedBy.focus === "function" &&
      document.body.contains(this.openedBy)
    ) {
      this.openedBy.focus();
    }
  }
}

if (!customElements.get("cart-drawer")) customElements.define("cart-drawer", CartDrawer);
